import React, { useState, useEffect, useCallback } from 'react';
import {
  Package,
  RefreshCw,
  Download,
  ChevronUp,
  ChevronDown,
  Filter,
  X,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { purchaseOrderLinesService } from '../../../../services/purchaseOrderLinesService';
import {
  POrderLineWithHeaderDto,
  PagedResult,
  POrderLinesQueryParams,
  POrderStatus,
} from '../../../../types/purchaseOrderLines';
import { FilterPanel } from './components/FilterPanel';
import { PurchaseOrderLineRow } from './components/PurchaseOrderLineRow';
import { PurchaseOrderLineDetailsModal } from './components/PurchaseOrderLineDetailsModal';
import { exportToExcel } from './utils/excelExport';

interface SortConfig {
  column: string;
  direction: 'asc' | 'desc';
}

interface PurchaseOrderLinesListProps {
  onBack?: () => void;
}

export const PurchaseOrderLinesList: React.FC<PurchaseOrderLinesListProps> = ({ onBack }) => {
  // State management
  const [data, setData] = useState<PagedResult<POrderLineWithHeaderDto> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showMoreColumns, setShowMoreColumns] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLine, setSelectedLine] = useState<POrderLineWithHeaderDto | null>(null);
  const [exporting, setExporting] = useState(false);

  // Query parameters
  const [queryParams, setQueryParams] = useState<POrderLinesQueryParams>({
    pageNumber: 1,
    pageSize: 50,
    sortColumn: 'OrderNo',
    sortDirection: 'desc',
  });

  // Filter state
  const [filters, setFilters] = useState<{
    itemNo: string;
    supplierNo: string;
    fromDate: string;
    toDate: string;
    maxOrderStatus: number | undefined;
  }>({
    itemNo: '',
    supplierNo: '',
    fromDate: '',
    toDate: '',
    maxOrderStatus: undefined,
  });

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await purchaseOrderLinesService.getPOrderLinesPaged(queryParams);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'En feil oppstod ved lasting av data');
      console.error('Error loading purchase order lines:', err);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle sorting
  const handleSort = (column: string) => {
    setQueryParams((prev) => ({
      ...prev,
      sortColumn: column,
      sortDirection:
        prev.sortColumn === column && prev.sortDirection === 'asc' ? 'desc' : 'asc',
      pageNumber: 1, // Reset to first page when sorting
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setQueryParams((prev) => ({
      ...prev,
      pageNumber: newPage,
    }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setQueryParams((prev) => ({
      ...prev,
      pageSize: newPageSize,
      pageNumber: 1, // Reset to first page when changing page size
    }));
  };

  // Handle filters
  const handleApplyFilters = () => {
    setQueryParams((prev) => ({
      ...prev,
      itemNo: filters.itemNo || undefined,
      supplierNo: filters.supplierNo || undefined,
      fromDate: filters.fromDate || undefined,
      toDate: filters.toDate || undefined,
      maxOrderStatus: filters.maxOrderStatus,
      pageNumber: 1, // Reset to first page when filtering
    }));
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilters({
      itemNo: '',
      supplierNo: '',
      fromDate: '',
      toDate: '',
      maxOrderStatus: undefined,
    });
    setQueryParams((prev) => ({
      pageNumber: 1,
      pageSize: prev.pageSize,
      sortColumn: prev.sortColumn,
      sortDirection: prev.sortDirection,
    }));
  };

  // Handle export
  const handleExport = async () => {
    if (!data || data.items.length === 0) {
      alert('Ingen data å eksportere');
      return;
    }

    try {
      setExporting(true);
      await exportToExcel(data.items, showMoreColumns);
    } catch (err) {
      console.error('Export error:', err);
      alert('Feil ved eksport: ' + (err instanceof Error ? err.message : 'Ukjent feil'));
    } finally {
      setExporting(false);
    }
  };

  // Handle row click
  const handleRowClick = (line: POrderLineWithHeaderDto) => {
    setSelectedLine(line);
  };

  // Render sort icon
  const renderSortIcon = (column: string) => {
    if (queryParams.sortColumn !== column) return null;
    return queryParams.sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  // Calculate active filters count
  const activeFiltersCount = [
    filters.itemNo,
    filters.supplierNo,
    filters.fromDate,
    filters.toDate,
    filters.maxOrderStatus !== undefined,
  ].filter(Boolean).length;

  // Loading indicator
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Laster bestillingslinjer...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackButton onClick={onBack || (() => {})} />
          <Package className="w-6 h-6 text-purple-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Bestillingslinjer</h1>
            <p className="text-sm text-gray-500 mt-1">
              Oversikt over alle bestillingslinjer med ordredetaljer
            </p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters
                  ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span>Filtrer</span>
              {activeFiltersCount > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setShowMoreColumns(!showMoreColumns)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors border-2 border-gray-200"
            >
              <Eye className="w-4 h-4" />
              <span>{showMoreColumns ? 'Skjul' : 'Vis'} flere kolonner</span>
            </button>

            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Oppdater
            </button>

            <button
              onClick={handleExport}
              disabled={exporting || !data || data.items.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Eksporterer...' : 'Eksporter til Excel'}
            </button>
          </div>

          {data && (
            <div className="text-sm text-gray-600">
              Viser {data.items.length} av {data.totalRecords} linjer
            </div>
          )}
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <FilterPanel
            filters={filters}
            onFiltersChange={setFilters}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            onClose={() => setShowFilters(false)}
          />
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span className="font-medium">Feil:</span>
          </div>
          <p className="text-red-600 mt-1">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {data && data.items.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">Ingen bestillingslinjer funnet</p>
            <p className="text-sm mt-2">Prøv å justere filtrene eller last inn data på nytt</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {/* Essential columns */}
                    <th
                      onClick={() => handleSort('OrderNoERP')}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Ordre (ERP)
                        {renderSortIcon('OrderNoERP')}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('LineNoERP')}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Linje
                        {renderSortIcon('LineNoERP')}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('ItemNo')}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Varenr.
                        {renderSortIcon('ItemNo')}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Beskrivelse
                    </th>
                    <th
                      onClick={() => handleSort('SupplierNo')}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Leverandør
                        {renderSortIcon('SupplierNo')}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('Quantity')}
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center justify-end gap-1">
                        Antall
                        {renderSortIcon('Quantity')}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('QuantityDelivered')}
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center justify-end gap-1">
                        Levert
                        {renderSortIcon('QuantityDelivered')}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gjenstår
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Framdrift
                    </th>
                    <th
                      onClick={() => handleSort('OrderDate')}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Ordredato
                        {renderSortIcon('OrderDate')}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('DeliveryDate')}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Leveringsdato
                        {renderSortIcon('DeliveryDate')}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('OrderStatus')}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {renderSortIcon('OrderStatus')}
                      </div>
                    </th>

                    {/* Additional columns (show more) */}
                    {showMoreColumns && (
                      <>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Pris
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rabatt
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Enhet
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Lager
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Batch
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Serie
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Stdlokasjon
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vår ref.
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.items.map((line) => (
                    <PurchaseOrderLineRow
                      key={line.id}
                      line={line}
                      showMoreColumns={showMoreColumns}
                      onClick={() => handleRowClick(line)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data && data.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  {/* Page size selector */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Linjer per side:</label>
                    <select
                      value={queryParams.pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>

                  {/* Page info */}
                  <div className="text-sm text-gray-600">
                    Side {data.pageNumber} av {data.totalPages} ({data.totalRecords} totalt)
                  </div>

                  {/* Pagination controls */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={!data.hasPreviousPage || loading}
                      className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Første side"
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handlePageChange(data.pageNumber - 1)}
                      disabled={!data.hasPreviousPage || loading}
                      className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Forrige side"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="px-4 py-2 text-sm font-medium">
                      {data.pageNumber}
                    </span>
                    <button
                      onClick={() => handlePageChange(data.pageNumber + 1)}
                      disabled={!data.hasNextPage || loading}
                      className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Neste side"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handlePageChange(data.totalPages)}
                      disabled={!data.hasNextPage || loading}
                      className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Siste side"
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      {selectedLine && (
        <PurchaseOrderLineDetailsModal
          line={selectedLine}
          onClose={() => setSelectedLine(null)}
        />
      )}
    </div>
  );
};
